import React, { useMemo, useState } from "react";
import ScrollTabs from "./ScrollTabs";
import { AttackDefenceTrend, MetricGroups, TacticalProfile, VenueSplit } from "../football-intelligence/TeamPanels";
import ShotExplorer from "../football-intelligence/ShotExplorer";
import HeatmapExplorer from "../football-intelligence/HeatmapExplorer";
import NetworkExplorer from "../football-intelligence/NetworkExplorer";
import { formatMetric, metricLabel } from "../football-intelligence/fiData";
import { getLeagueNameFromAny } from "../../utils/leagueRouting";
import "../football-intelligence/football-intelligence.css";
import "../football-intelligence/fi-extra.css";
import "./football-app.css";

export const usableSeasonSummary = summary => summary && Number(summary.played)>0 ? summary : null;

export function recordedFormTotal(rows,key){
  if(!rows.length || rows.some(r=>r[key]===null || r[key]===undefined || r[key]==='' || !Number.isFinite(Number(r[key]))))return '—';
  return rows.reduce((total,row)=>total+Number(row[key]),0);
}

const SAMPLE_SIZES = [5, 10, 20, 50, 1000];
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");
const f2 = (v) => (typeof v === "number" ? v.toFixed(2) : "—");

const compName = (fx) => getLeagueNameFromAny({ code: fx.league || fx.competition, name: fx.league_name }) || fx.league || fx.competition || "Competition";
const resultClass = (r) => (r === "W" ? "W" : r === "D" ? "D" : "L");

function ScoreChip({ fx }) {
  if (!fx.outcome) return <span className="fa-sub">{fmtTime(fx.date)}</span>;
  return <span className={`fa-res ${resultClass(fx.outcome)}`}>{fx.home_goals}–{fx.away_goals}</span>;
}

function FixtureRow({ fx, onOpen }) {
  return (
    <a className="fa-matchrow" href={`/fixture/${fx.fixture_id}`} onClick={(e) => { e.preventDefault(); onOpen(`/fixture/${fx.fixture_id}`); }} style={{ textDecoration: "none", color: "inherit" }}>
      <span className="d">{fmtDay(fx.date).split(" ").slice(1).join(" ")}<br />{fmtDay(fx.date).split(" ")[0]}</span>
      <span className="o"><b>{fx.home} v {fx.away}</b><span className="fa-sub">{compName(fx)}</span></span>
      <span className="r"><ScoreChip fx={fx} /></span>
    </a>
  );
}

function IntelFilters({ intel, filters, onFilter }) {
  const d = intel.data;
  if (!d || d.status === "UNAVAILABLE") return null;
  return (
    <div className="fa-filters">
      <label>Competition
        <select aria-label="Competition" value={filters.competition || ""} onChange={(e) => onFilter("competition", e.target.value)}>
          <option value="">All cached competitions</option>
          {(d.competitions || []).map((c) => <option key={c} value={c}>{d.competition_names?.[c] || c}</option>)}
        </select>
      </label>
      <label>Sample
        <select aria-label="Sample" value={filters.last_n || 10} onChange={(e) => onFilter("last_n", e.target.value)}>
          {SAMPLE_SIZES.map((n) => <option key={n} value={n}>{n === 1000 ? "Full cached season" : `Last ${n} matches`}</option>)}
        </select>
      </label>
    </div>
  );
}

const HEADLINE = ["xgf", "xga", "shots_for", "corners_for"];

function SampleNote({ data }) {
  if (!data || data.status !== "AVAILABLE") return null;
  return <p className="fa-note">PitchAPI sample: {data.sample_size} {data.sample_size === 1 ? "match" : "matches"}{data.date_range?.[0] ? ` · ${new Date(data.date_range[0]).toLocaleDateString("en-GB", { month: "short", year: "numeric" })} – ${new Date(data.date_range[1]).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}` : ""}. Historical actuals, not live; recorded provider data.</p>;
}

/**
 * Club profile on the shared football shell. Public data (API-Football
 * fixtures, form, squad, league season) shows for everyone; PitchAPI team
 * analytics (xG, tactics, shot maps, networks) only for administrators.
 */
export default function TeamProfileView({
  team, fixtures, recentForm, formWindow, seasonSummary: rawSeasonSummary, squad, season, tab, onTab, isAdmin, analyticsAllowed = isAdmin, intel, filters, onFilter, onNavigate, renderCommunity, renderNews, renderHighlights, controls, playerHref,
}) {
  const seasonSummary = usableSeasonSummary(rawSeasonSummary);
  const [per90] = useState(false);
  const data = intel.data && intel.data.status === "AVAILABLE" ? intel.data : null;
  const played = useMemo(() => fixtures.filter((f) => f.outcome).sort((a, b) => new Date(b.date) - new Date(a.date)), [fixtures]);
  const upcoming = useMemo(() => fixtures.filter((f) => !f.outcome && new Date(f.date) >= new Date(Date.now() - 3 * 3600e3)).sort((a, b) => new Date(a.date) - new Date(b.date)), [fixtures]);
  const next = upcoming[0] || null;
  const metrics = data?.metrics || {};

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "matches", label: "Matches" },
    { id: "squad", label: "Squad" },
    { id: "statistics", label: "Statistics" },
    ...(data ? [{ id: "tactics", label: "Tactics" }] : []),
    { id: "news", label: "News" },
    { id: "highlights", label: "Highlights" },
    { id: "community", label: "Community" },
  ];
  const current = tabs.some((t) => t.id === tab) ? tab : "overview";

  return (
    <div className="fa">
      <header className="fa-header">
        <div className="fa-wrap">
          <div className="fa-header-top">
            <button type="button" className="fa-back" onClick={() => onNavigate(-1)} aria-label="Go back">←</button>
            <div className="fa-header-meta"><strong>Club</strong><span>{team.venue?.name || ""}{team.venue?.city ? ` · ${team.venue.city}` : ""}</span></div>
          </div>
          <div className="fa-phead">
            {team.logo ? <img src={team.logo} alt="" style={{ width: 64, height: 64, objectFit: "contain", flex: "none" }} /> : <span className="fa-crest" style={{ width: 64, height: 64 }} aria-hidden="true">{team.name?.[0]}</span>}
            <div style={{ minWidth: 0 }}>
              <h1>{team.name}</h1>
              <div className="fa-sub">{season} season{team.venue?.capacity ? ` · ${Number(team.venue.capacity).toLocaleString("en-GB")} capacity` : ""}</div>
            </div>
          </div>
        </div>
      </header>
      <ScrollTabs tabs={tabs} value={current} onChange={onTab} label="Club sections" idPrefix="fa-club" />
      <div className="fa-wrap" role="tabpanel" id={`fa-club-panel-${current}`} aria-labelledby={`fa-club-tab-${current}`}>
        {current === "overview" && (
          <>
            {next && (
              <section className="fa-card" aria-label="Next match">
                <h3>Next match</h3>
                <a className="fa-matchrow" href={`/fixture/${next.fixture_id}`} onClick={(e) => { e.preventDefault(); onNavigate(`/fixture/${next.fixture_id}`); }} style={{ textDecoration: "none", color: "inherit" }}>
                  <span className="d">{fmtDay(next.date)}</span>
                  <span className="o"><b>{next.home} v {next.away}</b><span className="fa-sub">{compName(next)}</span></span>
                  <span className="r"><b>{fmtTime(next.date)}</b></span>
                </a>
              </section>
            )}
            <section className="fa-card" aria-label="Team form">
              <h3>Team form</h3>
              {recentForm.length ? (
                <div className="fa-tiles" style={{ gridTemplateColumns: `repeat(${Math.min(5, recentForm.length)}, 1fr)` }}>
                  {recentForm.slice(0, 5).map((m) => (
                    <a key={`${m.fixture_id}-${m.date}`} className="fa-tile" href={`/fixture/${m.fixture_id}`} onClick={(e) => { e.preventDefault(); onNavigate(`/fixture/${m.fixture_id}`); }} style={{ textAlign: "center", textDecoration: "none", color: "inherit", padding: "10px 4px" }} title={`${m.result} vs ${m.opponent}`}>
                      <span className={`fa-res ${resultClass(m.result)}`} style={{ margin: "0 auto" }}>{m.score_for}–{m.score_against}</span>
                      <span className="fa-sub" style={{ display: "block", marginTop: 6, fontSize: 11, textTransform: "none", letterSpacing: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.opponent}</span>
                    </a>
                  ))}
                </div>
              ) : <div className="fa-empty">No recent results recorded.</div>}
              <p className="fa-note">All competitions · last {formWindow} matches (API-Football).</p>
            </section>
            <div className="fa-twocol">
              <section className="fa-card" aria-label="Goals">
                <h3>Goals</h3>
                <div className="fa-tiles">
                  <div className="fa-tile"><b>{recordedFormTotal(recentForm,"score_for")}</b><span>Scored</span></div>
                  <div className="fa-tile"><b>{recordedFormTotal(recentForm,"score_against")}</b><span>Conceded</span></div>
                </div>
                <p className="fa-note">Last {formWindow} matches, all competitions (API-Football).</p>
              </section>
              {seasonSummary && (
                <section className="fa-card" aria-label="League season">
                  <h3>League season</h3>{seasonSummary.warning && <p className="fa-note">{seasonSummary.warning}</p>}
                  <span className="fa-scope">{season} · league only</span>
                  <div className="fa-tiles four">
                    <div className="fa-tile"><b>{seasonSummary.played}</b><span>Played</span></div>
                    <div className="fa-tile"><b>{seasonSummary.wins}-{seasonSummary.draws}-{seasonSummary.losses}</b><span>W-D-L</span></div>
                    <div className="fa-tile"><b>{seasonSummary.gf}-{seasonSummary.ga}</b><span>GF-GA</span></div>
                    <div className="fa-tile"><b>{f2(seasonSummary.avgGf)}</b><span>GF/g</span></div>
                  </div>
                </section>
              )}
            </div>
            {data && metrics.xgf && (
              <section className="fa-card" aria-label="Expected goals">
                <h3>Expected goals</h3>
                <span className="fa-scope">PitchAPI · {data.sample_size} matches{data.date_range?.[1] ? ` · to ${new Date(data.date_range[1]).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}` : ""}</span>
                <div className="fa-tiles">
                  <div className="fa-tile"><b>{f2(metrics.xgf.value)}</b><span>xG / match</span></div>
                  {metrics.xga && <div className="fa-tile"><b>{f2(metrics.xga.value)}</b><span>xGA / match</span></div>}
                </div>
                <p className="fa-note">A separate sample from the goals above, shown on its own so the two are never read as one. Recorded provider data.</p>
              </section>
            )}
            {upcoming.length > 1 && (
              <section className="fa-card" aria-label="Upcoming fixtures">
                <h3>Upcoming</h3>
                {upcoming.slice(0, 4).map((fx) => <FixtureRow key={fx.fixture_id} fx={fx} onOpen={onNavigate} />)}
              </section>
            )}
            {played.length > 0 && (
              <section className="fa-card" aria-label="Recent results">
                <h3>Recent results</h3>
                {played.slice(0, 5).map((fx) => <FixtureRow key={fx.fixture_id} fx={fx} onOpen={onNavigate} />)}
                <button type="button" className="fa-btn" style={{ width: "100%", marginTop: 8 }} onClick={() => onTab("matches")}>All matches</button>
              </section>
            )}
            <details className="fa-card"><summary style={{ cursor: "pointer", fontWeight: 800 }}>Season &amp; form window</summary><div style={{ marginTop: 10 }}>{controls}</div></details>
          </>
        )}
        {current === "matches" && (
          <section className="fa-card" aria-label="Matches">
            <h3>{season} matches</h3>
            {fixtures.length ? fixtures.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).map((fx) => <FixtureRow key={fx.fixture_id} fx={fx} onOpen={onNavigate} />) : <div className="fa-empty">No matches found for {season}.</div>}
          </section>
        )}
        {current === "squad" && (
          <section className="fa-card" aria-label="Squad">
            <h3>Squad · {season}</h3>
            {squad.length ? (
              <ul className="fa-list">
                {squad.map((entry, i) => {
                  const p = entry.player || entry;
                  const pid = p.id || p.player_id;
                  const href = pid ? playerHref(pid) : null;
                  const body = (
                    <>
                      {p.photo || p.image ? <img src={p.photo || p.image} alt="" loading="lazy" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} /> : <span className="fa-crest" style={{ width: 40, height: 40 }}>{(p.name || "P")[0]}</span>}
                      <span style={{ minWidth: 0 }}><b>{p.name || p.player_name}</b><span className="fa-sub" style={{ display: "block" }}>{entry.position || p.position || entry.group || "Player"}</span></span>
                    </>
                  );
                  return <li key={pid || i}>{href ? <button type="button" onClick={() => onNavigate(href)}>{body}</button> : <div className="fa-matchrow" style={{ gridTemplateColumns: "40px 1fr" }}>{body}</div>}</li>;
                })}
              </ul>
            ) : <div className="fa-empty">Squad data is not available for this season yet.</div>}
            {!playerHref(0) && <p className="fa-note">Profiles open from a match; no recent match is available to link from.</p>}
          </section>
        )}
        {current === "statistics" && (
          <>
            {seasonSummary ? (
              <section className="fa-card" aria-label="League season statistics">
                <h3>League season</h3>{seasonSummary.warning && <p className="fa-note">{seasonSummary.warning}</p>}
                <span className="fa-scope">{seasonSummary.source === "pitchapi" ? "Recorded matches" : "Source · API-Football"} · {season} · league only</span>
                <div className="fa-tiles four">
                  <div className="fa-tile"><b>{seasonSummary.played}</b><span>Played</span></div>
                  <div className="fa-tile"><b>{seasonSummary.wins}-{seasonSummary.draws}-{seasonSummary.losses}</b><span>W-D-L</span></div>
                  <div className="fa-tile"><b>{seasonSummary.gf}-{seasonSummary.ga}</b><span>GF-GA</span></div>
                  <div className="fa-tile"><b>{f2(seasonSummary.avgGf)}</b><span>GF/g</span></div>
                </div>
              </section>
            ) : <div className="fa-empty">League season statistics are not available for this club yet.</div>}
            {analyticsAllowed && (
              <section className="fa-card fa-darkembed" aria-label="Detailed team statistics">
                <h3>Detailed statistics</h3>
                <span className="fa-scope">Source · PitchAPI · sampled matches, not season totals</span>
                <IntelFilters intel={intel} filters={filters} onFilter={onFilter} />
                {intel.loading && <div className="fa-skeleton" role="status" aria-label="Loading statistics" style={{ height: 80 }} />}
                {data ? (
                  <>
                    <div className="fa-tiles">{HEADLINE.filter((k) => metrics[k]).map((k) => <div className="fa-tile" key={k}><b>{formatMetric(k, metrics[k].value, true)}</b><span>{metricLabel(k)}</span></div>)}</div>
                    <AttackDefenceTrend matches={data.matches || []} />
                    <VenueSplit matches={data.matches || []} />
                    <MetricGroups kind="team" metrics={metrics} matches={data.matches || []} per90={per90} />
                    <SampleNote data={data} />
                  </>
                ) : !intel.loading && <div className="fa-empty">{intel.error || "No verified detailed statistics for this club (no identity mapping or cached matches)."}</div>}
              </section>
            )}
          </>
        )}
        {current === "tactics" && data && (
          <section className="fa-card fa-darkembed" aria-label="Tactics">
            <h3>Tactical profile</h3>
            <IntelFilters intel={intel} filters={filters} onFilter={onFilter} />
            <TacticalProfile metrics={metrics} />
            <h3 style={{ marginTop: 18 }}>Shot map</h3>
            <ShotExplorer shots={data.shots || []} matchOptions={(data.matches || []).map((r) => ({ fixture_id: r.fixture_id, name: r.fixture?.name || String(r.fixture_id) }))} heading="" />
            <h3 style={{ marginTop: 18 }}>Action density</h3>
            <HeatmapExplorer heatmaps={data.heatmaps || []} matchName={(id) => (data.matches || []).find((r) => String(r.fixture_id) === String(id))?.fixture?.name || ""} sampleSize={data.sample_size} />
            <h3 style={{ marginTop: 18 }}>Passing networks</h3>
            <NetworkExplorer networks={data.networks || []} matchName={(id) => (data.matches || []).find((r) => String(r.fixture_id) === String(id))?.fixture?.name || ""} sampleSize={data.sample_size} />
            <SampleNote data={data} />
          </section>
        )}
        {current === "news" && <div className="fa-card">{renderNews()}</div>}
        {current === "highlights" && <div className="fa-card">{renderHighlights()}</div>}
        {current === "community" && <div className="fa-card">{renderCommunity()}</div>}
      </div>
    </div>
  );
}
